import { GoogleAnalytics } from "../../resolvers-types";
import { evaluateObjectBindings, getParentComponent, getScope, getState } from "./dataService";
import { log } from "./logger";
import { getTypename } from "./metadataUtils";
import { client } from "./nats-client";
import { AnalyticsConfig, getTenant, getTenantConfig } from "./tenantUtils";
import tracing from "./tracing";
import { generateGuid, getDeviceCategory, getPlatform, getTestId, getUtcTimestamp } from "./utils";

// Suggested Event Categories and Names
//
// | Category       | Purpose                             | Event Names                                       |
// | -------------- | ----------------------------------- | ------------------------------------------------- |
// | navigation     | Page views, route changes, tabs     | `menu_click`                                      |
// |                |                                     | `page_view`                                       |
// |                |                                     | `tab_click`                                       |
// | -------------- | ----------------------------------- | ------------------------------------------------- |
// | engagement     | User interaction with UI            | `accordion_group_change`                          |
// |                |                                     | `avatar_change`                                   |
// |                |                                     | `button_click`                                    |
// |                |                                     | `container_click`                                 |
// |                |                                     | `repeater_row_click`                              |
// |                |                                     | `checkbox_change`                                 |
// |                |                                     | `date_picker_change`                              |
// |                |                                     | `datetime_picker_change`                          |
// |                |                                     | `image_click`                                     |
// |                |                                     | `input_blur`                                      |
// |                |                                     | `input_focus`                                     |
// |                |                                     | `label_click`                                     |
// |                |                                     | `link_click`                                      |
// |                |                                     | `media_complete`                                  |
// |                |                                     | `media_pause`                                     |
// |                |                                     | `media_play`                                      |
// |                |                                     | `media_progress`                                  |
// |                |                                     | `modal_open`                                      |
// |                |                                     | `model_close`                                     |
// |                |                                     | `multi_select_change`                             |
// |                |                                     | `radio_group_change`                              |
// |                |                                     | `send_sms`                                        |
// |                |                                     | `send_email`                                      |
// |                |                                     | `slider_change`                                   |
// |                |                                     | `switch_change`                                   |
// |                |                                     | `timepicker_change`                               |
// | -------------- | ----------------------------------- | ------------------------------------------------- |
// | auth           | User login and signup               | `become_user`                                     |
// |                |                                     | `login`                                           |
// |                |                                     | `logout`                                          |
// |                |                                     | `setup_mfa`                                       |
// |                |                                     | `update_password`                                 |
// | -------------- | ----------------------------------- | ------------------------------------------------- |
// | content        | Viewing or selecting content        | `file_download`                                   |
// |                |                                     | `file_upload_file_add`                            |
// |                |                                     | `file_upload_file_remove`                         |
// |                |                                     | `video_play`                                      |
// | -------------- | ----------------------------------- | ------------------------------------------------- |
// | sharing        | Social or internal content sharing  | `share_watchlist`                                 |
// |                |                                     | `shared_document`                                 |
// | -------------- | ----------------------------------- | ------------------------------------------------- |
// | search         | Searches and filtering              | `filter_selection`                                |
// |                |                                     | `search`                                          |
// |                |                                     | `sort_change`                                     |
// | -------------- | ----------------------------------- | ------------------------------------------------- |
// | error          | UI or business logic errors         | `api_error`                                       |
// |                |                                     | `validation_error`                                |
// | -------------- | ----------------------------------- | ------------------------------------------------- |
// | settings       | Preferences, toggles, account edits | `toggle_dark_mode`                                |
// |                |                                     | `update_profile`                                  |
// | -------------- | ----------------------------------- | ------------------------------------------------- |
// | notification   | Alerts, popups, and messages        | `notification_click`                              |
// |                |                                     | `toast_shown`                                     |
// | -------------- | ----------------------------------- | ------------------------------------------------- |
// | system         | Background or automatic events      | `session_start`                                   |
// |                |                                     | `user_engagement`                                 |
// -----------------------------------------------------------------------------------------------------------|

export type UserAction = {
  Id: string; // GUID will be used as UserActionId on service calls

  EventName: string; // see table above
  EventCategory?: string; // see table above

  PageId: string;
  PageTitle: string;
  PageReferrer: string;
  PageUrl: string;
  PagePath: string;
  PageSerialNumber: string; // Serial number of the page

  SessionId: string; // Session identifier for tracking GUID
  ScreenTime?: number; // in milliseconds
  SessionTime?: number; // in milliseconds
  Timestamp: Date; // UTC timestamp of the event

  UserId: string; // GUID
  ImpersonationId: string; // GUID
  TenantId: string;
  TenantIdentifier: string;

  Locale: string; // e.g., 'en-US', 'fr-FR'
  DeviceCategory: string; // e.g., 'desktop', 'mobile', 'tablet'

  Platform: string; // e.g., 'web', 'android', 'ios'
  Browser: string; // e.g., UserAgent string
  ScreenResolution: string; // e.g., '1920x1080'
  ViewResolution: string; // e.g., '1920x1080'

  ElementId?: string; // ID of the HTML element interacted with
  ElementType: string; // e.g., 'Button', 'MultiSelect', InputField
  ElementClass?: string; // Class of the HTML element interacted with
  ElementText?: string; // Text content of the element (if applicable)
  LinkTarget?: string; // _blank, _self, _parent, _top
  LinkUrl?: string; // URL of the link clicked (if applicable)
  Outbound?: boolean; // true if the link is an outbound link

  ComponentId: string; // Id of the surrounding component. E.g. watchlist-advanced
  ComponentInstanceId: string; // watchlist-advanced-123423
  ComponentSerialNumber: string; // Serial number of the component instance

  MarketAppId: string; // Id of the app (if applicable)
  MarketAppName: string;

  Path: { Id: string; Type: string }[] | string; // Path to the current element

  Value?: string; // Value of the element (if applicable). E.g. dropdown value, input value

  VersionATier: string;
  VersionPTier: string;
  VersionNative: string; // Mobile app version (if applicable)

  AppName: string; // Mobile app name (if applicable)

  Metadata?: Record<string, any>; // Additional metadata for custom events
  IsImplicit: boolean; // true if the event is not explicitly triggered by the user, e.g., page view, auto select
};

//
// Page
// - Component
//    - Control (button, tab container)

// App - Component
// - Element - Component
//     - HTML Element - Element

// eventName: market-apps-analytics
// parameters: {
//
// 	"marketAppId":"03ba5052-aa2d-45dc-a832-057449f927d9",
// 	"marketAppName":"Indices",
// 	"componentId":"83e497f8-4846-44ae-89c4-15479826fd1d",
// 	"componentName":"Some name for the element",
// 	"elementType":"Timeframe Selector",
//        "elementId":"something that uniquely identifies the element/control"
// 	"elementAction":"click",
// 	"elementValue:"6m"
// }

const tag = "analytics";
let analytics: AnalyticsConfig | null = null;
let analyticsSetup = false;

export function startPageTimer() {
  performance.mark("pageStart");
}

export function getPageTime() {
  const marks = performance.getEntriesByName("pageStart");
  if (marks.length === 0) {
    return 0; // Return 0 or a default value if the mark doesn't exist
  }
  return Math.floor(performance.measure("pageTime", "pageStart").duration);
}

/**
 * Publishes a page_view audit record immediately for route loading
 * This should be called in route loaders to establish context before page renders
 */
export function publishPageViewOnLoad(pageConfig, context) {
  if (!analyticsSetup) return null;
  if (!pageConfig || !context) return null;

  startPageTimer(); // Start timing for this page

  const action = publishUserAction(pageConfig, context, "page_view", null, {
    EventCategory: "navigation",
    IsImplicit: true,
  });

  if (action) {
    tracing.setContextAction(action);
  }

  return action;
}

export function publishUserAction(config, context, trigger, event, data) {
  if (!analyticsSetup) return null;

  const isEditor = window.location.pathname.includes("editor-gui");
  if (isEditor) {
    // does not run analytics in editor
    return null;
  }

  const id = generateGuid();
  // const config = context.Path[context.Path.length - 1]?.Config;

  if (!config) {
    console.error("No config found for the current context");
    return;
  }

  const type = getTypename(config) ?? "Unknown";
  const pageConfig = type == "Page" ? config : context.Path[0]?.Config;

  const path =
    context.Path?.map((item) => ({ Id: item.Config.Id, Type: getTypename(item.Config) })).filter((x) =>
      ["Page", "Component", "Dialog", "TabContainer", "Feed"].includes(x.Type)
    ) ?? [];

  // also include path from data if provided
  const populatedPath = Array.isArray(data.Path) ? path.concat(data.Path) : path;

  const { global, state, form, parentState } = getState(context);
  const scope = getScope(context, config, state, form, event, parentState);
  const releaseInfo = global.ReleaseInfo.value;
  const parentComp = getParentComponent(context);

  const analytics = config.Analytics ?? {};

  const evaluatedAnalytics = evaluateObjectBindings({ obj: analytics, scope });
  evaluatedAnalytics["EventName"] = evaluatedAnalytics["EventName"] || data.EventName || trigger;
  evaluatedAnalytics["EventCategory"] = evaluatedAnalytics["EventCategory"] || data.EventCategory || "engagement";
  evaluatedAnalytics["Metadata"] = evaluatedAnalytics["Metadata"] || data.Metadata || {};

  const userAction: UserAction = {
    Id: id,
    Timestamp: getUtcTimestamp(),
    ElementId: getTestId(config),
    ElementType: type,
    PageTitle: pageConfig?.Label ?? pageConfig?.Name ?? "",
    PageId: pageConfig?.PageUrl ?? pageConfig?.Id,
    PageUrl: window.location.href,
    PagePath: window.location.pathname,
    PageSerialNumber: pageConfig?.SerialNumber,
    PageReferrer: document.referrer,
    SessionId: tracing.getSessionId() ?? "",
    ScreenTime: getPageTime(),
    SessionTime: tracing.getSessionElapsedTime(),
    UserId: global.User.value?.Id ?? "",
    ImpersonationId: global.Impersonation.value?.UserId,
    TenantId: global.User.value?.TenantId,
    TenantIdentifier: getTenant(),
    Locale: global.Locale.value,
    DeviceCategory: getDeviceCategory(),
    Platform: getPlatform(),
    Browser: navigator.userAgent,
    ScreenResolution: `${window.screen.width}x${window.screen.height}`,
    ViewResolution: `${window.innerWidth}x${window.innerHeight}`,
    ComponentId: parentComp?.ComponentId,
    ComponentInstanceId: parentComp?.Id,
    ComponentSerialNumber: parentComp?.SerialNumber,
    VersionATier: releaseInfo?.ATier?.Release,
    VersionPTier: releaseInfo?.PTier?.Release,
    VersionNative: window.NativeApp?.Version,
    AppName: window.NativeApp?.Name,
    Metadata: config.Analytics?.Metadata ?? {},
    IsImplicit: false,
    ...data,
    ...evaluatedAnalytics,
    Path: populatedPath,
  };

  log.info(tag, "Publishing user action", userAction);
  // log.groupInfo(tag, "Publishing user action");
  // log.info(tag, "Config:", config);
  // log.info(tag, "Page Config:", pageConfig);
  // log.info(tag, "Trigger:", trigger);
  // log.info(tag, "Event:", event);
  // log.info(tag, "Data:", data);
  // log.info(tag, "Context:", context);
  // log.info(tag, "Node:", node);
  // log.info(tag, "UserAction:", userAction);
  // log.groupEnd();


  tracing.setContextAction(userAction);

  if (
    !global.TenantConfig.value?.CookieConsent?.Enabled ||
    (global.User.value?.Consent !== null && global.User.value?.Consent?.Analytics)
  ) {
    publishInternal(userAction);
    pushToProviders(userAction);
  }

  return userAction;
}

function publishInternal(userAction: UserAction) {
  const requestId = generateGuid();
  const audit = {
    Id: generateGuid(),
    TenantId: userAction.TenantId,
    EntityId: userAction.Id,
    EntityType: "UserAction",
    Operation: userAction.EventName,
    RequestId: requestId,
    SessionId: userAction.SessionId,
    ActionId: userAction.Id,
    UserId: userAction.UserId,
    ImpersonationId: userAction.ImpersonationId,
    Locale: userAction.Locale,
    Metadata: userAction.Metadata,
    ScreenTime: userAction.ScreenTime,
    SessionTime: userAction.SessionTime,
    DeviceCategory: userAction.DeviceCategory,
    Platform: userAction.Platform,
    Browser: userAction.Browser,
    ScreenResolution: userAction.ScreenResolution,
    ViewResolution: userAction.ViewResolution,
    ElementId: userAction.ElementId,
    ElementType: userAction.ElementType,
    ElementClass: userAction.ElementClass,
    ElementText: userAction.ElementText,
    LinkTarget: userAction.LinkTarget,
    LinkUrl: userAction.LinkUrl,
    Outbound: userAction.Outbound,
    MarketAppId: userAction.MarketAppId,
    MarketAppName: userAction.MarketAppName,
    AppName: userAction.AppName,
    Path: userAction.Path,
    ClientTimestamp: userAction.Timestamp,
    EventName: userAction.EventName,
    EventCategory: userAction.EventCategory,
    PageId: userAction.PageId,
    PageUrl: userAction.PageUrl,
    PageTitle: userAction.PageTitle,
    PageSerialNumber: userAction.PageSerialNumber,
    ComponentId: userAction.ComponentId,
    ComponentSerialNumber: userAction.ComponentSerialNumber,
    VersionATier: userAction.VersionATier,
    VersionPTier: userAction.VersionPTier,
    Value: userAction.Value,
    IsImplicit: userAction.IsImplicit,
  };

  const tracingHeaders = tracing.getHeaders(userAction);

  log.info(tag, "Publishing audit", audit);

  client.publish("audit.create", audit, { ...tracingHeaders, "NG-ActionId": userAction.Id });
}

function pushToProviders(userAction: UserAction) {
  if (analytics) {
    const ga = analytics["GoogleAnalytics"];
    if (ga?.IsEnabled) {
      pushToGoogleAnalytics(userAction);
    }
  }
}

export function setupAnalytics() {
  const tenantConfig = getTenantConfig();
  analytics = tenantConfig?.analytics ?? null;

  log.info(tag, "Analytics configuration", analytics);

  if (analytics) {
    const ga = analytics["GoogleAnalytics"];
    if (ga?.IsEnabled) {
      setupGoogleAnalytics(ga);
    }
  }

  analyticsSetup = true;
}

function setupGoogleAnalytics(provider: GoogleAnalytics) {
  const { TagId } = provider;

  if (!TagId) {
    log.error(tag, "Google Tag Manager ID is not provided while provider is enabled");
    return;
  }

  const script1 = document.createElement("script");
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${TagId}`;
  document.head.appendChild(script1);

  const script2 = document.createElement("script");
  script2.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${TagId}');
    `;
  document.head.appendChild(script2);
}

function pushToGoogleAnalytics(userAction: UserAction) {
  if (!window.gtag) {
    log.error(tag, "Google Analytics is not initialized");
    return;
  }
  const metadataEntries = userAction.Metadata ? Object.entries(userAction.Metadata) : [];
  const metadataObject = metadataEntries.reduce((acc, [key, value]) => {
    acc[`metadata_${key}`] = value;
    return acc;
  }, {});

  const eventData = {
    event_category: userAction.EventCategory,
    value: userAction.Value,
    page_title: userAction.PageTitle,
    page_location: userAction.PageUrl,
    page_path: userAction.PagePath,
    page_id: userAction.PageId,
    page_serial_number: userAction.PageSerialNumber,
    user_id: userAction.UserId,
    impersonation_id: userAction.ImpersonationId,
    tenant_id: userAction.TenantId,
    tenant_identifier: userAction.TenantIdentifier,
    session_id: userAction.SessionId,
    screen_time: userAction.ScreenTime,
    session_time: userAction.SessionTime,
    timestamp: userAction.Timestamp?.toISOString ? userAction.Timestamp.toISOString() : userAction.Timestamp,
    locale: userAction.Locale || "en-US",
    device_category: userAction.DeviceCategory,
    platform: userAction.Platform,
    browser: userAction.Browser,
    screen_resolution: userAction.ScreenResolution,
    view_resolution: userAction.ViewResolution,
    element_id: userAction.ElementId,
    element_type: userAction.ElementType,
    element_class: userAction.ElementClass,
    element_text: userAction.ElementText,
    link_target: userAction.LinkTarget,
    link_url: userAction.LinkUrl,
    outbound: userAction.Outbound,
    component_id: userAction.ComponentId,
    component_instance_id: userAction.ComponentInstanceId,
    component_serial_number: userAction.ComponentSerialNumber,
    market_app_id: userAction.MarketAppId,
    market_app_name: userAction.MarketAppName,
    version_atier: userAction.VersionATier,
    version_ptier: userAction.VersionPTier,
    version_native: userAction.VersionNative,
    app_name: userAction.AppName,
    is_implicit: userAction.IsImplicit,
    path: Array.isArray(userAction.Path)
      ? userAction.Path.map((x) => `${x.Type}:${x.Id}`).join("/")
      : userAction.Path ?? "",
    ...metadataObject,
  };

  log.info(tag, "Publishing to Google Analytics", userAction.EventName, eventData);

  window.gtag("event", userAction.EventName, eventData);
}
